/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  ActivateObjectParams,
  ActivateObjectPathParameters,
  CreateObjectParams,
  CreateTransportParams,
  ExpandPackageParams,
  ExpandPackagePathParameters,
  GetObjectChildrenParams,
  GetObjectChildrenPathParameters,
  GetObjectMetadataParams,
  GetObjectMetadataPathParameters,
  GetObjectSourceParams,
  GetObjectSourcePathParameters,
  GetObjectTransportCandidatesParams,
  GetObjectTransportInfoParams,
  GetObjectTransportInfoPathParameters,
  GetPrettyPrintSettingsParams,
  GetSapGuiUrlParams,
  GetTableDataPreviewParams,
  GetTableDataPreviewPathParameters,
  GetTransportParams,
  GetTransportPathParameters,
  GetUsageReferencesParams,
  GetUsageReferencesPathParameters,
  ListProgramSubobjectsPathParameters,
  ListTransportsParams,
  NavigateFromSourceParams,
  NavigateFromSourcePathParameters,
  PrettyPrintObjectParams,
  PrettyPrintObjectPathParameters,
  SaveObjectSourceParams,
  SaveObjectSourcePathParameters,
  SearchFunctionGroupsParams,
  SearchMyObjectsParams,
  SearchPackagesParams,
  SyntaxCheckObjectParams,
  SyntaxCheckObjectPathParameters,
  TLFunctionGroupSearchResponse,
  TLHTTPValidationError,
  TLNamingConventionsResponse,
  TLNavigateRequest,
  TLNavigateResponse,
  TLObjectActivateResponse,
  TLObjectChildrenResponse,
  TLObjectCreateRequest,
  TLObjectCreateResponse,
  TLObjectMetadataResponse,
  TLObjectSourceResponse,
  TLObjectSourceUpdateRequest,
  TLObjectSourceUpdateResponse,
  TLObjectTransportCandidatesResponse,
  TLObjectTransportInfoResponse,
  TLPackageSearchResponse,
  TLPrettyPrintRequest,
  TLPrettyPrintResponse,
  TLPrettyPrintSettings,
  TLProjectExplorerSyntaxCheckRequest,
  TLProjectExplorerSyntaxCheckResponse,
  TLProjectExplorerTreeResponse,
  TLSapGuiLinkResponse,
  TLSaveAsProgramsRequest,
  TLSaveAsProgramsResponse,
  TLSubobjectListResponse,
  TLTableDataPreviewResponse,
  TLTransportCreateRequest,
  TLTransportInfo,
  TLTransportListResponse,
  TLUsageReferencesResponse,
  UpdatePrettyPrintSettingsParams,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Search Function Groups
 */
export const searchFunctionGroups = (
  params: SearchFunctionGroupsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLFunctionGroupSearchResponse>(
    { url: `/project-explorer/function-groups`, method: 'GET', params, signal },
    options,
  );
};

export const getSearchFunctionGroupsQueryKey = (params?: SearchFunctionGroupsParams) => {
  return ['project-explorer', 'function-groups', ...(params ? [params] : [])] as const;
};

export const getSearchFunctionGroupsQueryOptions = <
  TData = Awaited<ReturnType<typeof searchFunctionGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: SearchFunctionGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchFunctionGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSearchFunctionGroupsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof searchFunctionGroups>>> = ({ signal }) =>
    searchFunctionGroups(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof searchFunctionGroups>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SearchFunctionGroupsQueryResult = NonNullable<Awaited<ReturnType<typeof searchFunctionGroups>>>;
export type SearchFunctionGroupsQueryError = ErrorType<TLHTTPValidationError>;

export function useSearchFunctionGroups<
  TData = Awaited<ReturnType<typeof searchFunctionGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: SearchFunctionGroupsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchFunctionGroups>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchFunctionGroups>>,
          TError,
          Awaited<ReturnType<typeof searchFunctionGroups>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchFunctionGroups<
  TData = Awaited<ReturnType<typeof searchFunctionGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: SearchFunctionGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchFunctionGroups>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchFunctionGroups>>,
          TError,
          Awaited<ReturnType<typeof searchFunctionGroups>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchFunctionGroups<
  TData = Awaited<ReturnType<typeof searchFunctionGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: SearchFunctionGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchFunctionGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Search Function Groups
 */

export function useSearchFunctionGroups<
  TData = Awaited<ReturnType<typeof searchFunctionGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: SearchFunctionGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchFunctionGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSearchFunctionGroupsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Search Function Groups
 */
export const prefetchSearchFunctionGroupsQuery = async <
  TData = Awaited<ReturnType<typeof searchFunctionGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params: SearchFunctionGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchFunctionGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSearchFunctionGroupsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Search Function Groups
 */
export const invalidateSearchFunctionGroups = async (
  queryClient: QueryClient,
  params: SearchFunctionGroupsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getSearchFunctionGroupsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Search My Objects
 */
export const searchMyObjects = (
  params?: SearchMyObjectsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLProjectExplorerTreeResponse>(
    { url: `/project-explorer/my-objects`, method: 'GET', params, signal },
    options,
  );
};

export const getSearchMyObjectsQueryKey = (params?: SearchMyObjectsParams) => {
  return ['project-explorer', 'my-objects', ...(params ? [params] : [])] as const;
};

export const getSearchMyObjectsQueryOptions = <
  TData = Awaited<ReturnType<typeof searchMyObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: SearchMyObjectsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchMyObjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSearchMyObjectsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof searchMyObjects>>> = ({ signal }) =>
    searchMyObjects(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof searchMyObjects>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SearchMyObjectsQueryResult = NonNullable<Awaited<ReturnType<typeof searchMyObjects>>>;
export type SearchMyObjectsQueryError = ErrorType<TLHTTPValidationError>;

export function useSearchMyObjects<
  TData = Awaited<ReturnType<typeof searchMyObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | SearchMyObjectsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchMyObjects>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchMyObjects>>,
          TError,
          Awaited<ReturnType<typeof searchMyObjects>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchMyObjects<
  TData = Awaited<ReturnType<typeof searchMyObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: SearchMyObjectsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchMyObjects>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchMyObjects>>,
          TError,
          Awaited<ReturnType<typeof searchMyObjects>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchMyObjects<
  TData = Awaited<ReturnType<typeof searchMyObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: SearchMyObjectsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchMyObjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Search My Objects
 */

export function useSearchMyObjects<
  TData = Awaited<ReturnType<typeof searchMyObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: SearchMyObjectsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchMyObjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSearchMyObjectsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Search My Objects
 */
export const prefetchSearchMyObjectsQuery = async <
  TData = Awaited<ReturnType<typeof searchMyObjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: SearchMyObjectsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchMyObjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSearchMyObjectsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Search My Objects
 */
export const invalidateSearchMyObjects = async (
  queryClient: QueryClient,
  params?: SearchMyObjectsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getSearchMyObjectsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Get Naming Conventions
 */
export const getNamingConventions = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLNamingConventionsResponse>(
    { url: `/project-explorer/naming-conventions`, method: 'GET', signal },
    options,
  );
};

export const getGetNamingConventionsQueryKey = () => {
  return ['project-explorer', 'naming-conventions'] as const;
};

export const getGetNamingConventionsQueryOptions = <
  TData = Awaited<ReturnType<typeof getNamingConventions>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNamingConventions>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetNamingConventionsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getNamingConventions>>> = ({ signal }) =>
    getNamingConventions(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getNamingConventions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetNamingConventionsQueryResult = NonNullable<Awaited<ReturnType<typeof getNamingConventions>>>;
export type GetNamingConventionsQueryError = ErrorType<unknown>;

export function useGetNamingConventions<
  TData = Awaited<ReturnType<typeof getNamingConventions>>,
  TError = ErrorType<unknown>,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNamingConventions>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNamingConventions>>,
          TError,
          Awaited<ReturnType<typeof getNamingConventions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetNamingConventions<
  TData = Awaited<ReturnType<typeof getNamingConventions>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNamingConventions>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNamingConventions>>,
          TError,
          Awaited<ReturnType<typeof getNamingConventions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetNamingConventions<
  TData = Awaited<ReturnType<typeof getNamingConventions>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNamingConventions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Naming Conventions
 */

export function useGetNamingConventions<
  TData = Awaited<ReturnType<typeof getNamingConventions>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNamingConventions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetNamingConventionsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Naming Conventions
 */
export const prefetchGetNamingConventionsQuery = async <
  TData = Awaited<ReturnType<typeof getNamingConventions>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNamingConventions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetNamingConventionsQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Naming Conventions
 */
export const invalidateGetNamingConventions = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetNamingConventionsQueryKey() }, options);

  return queryClient;
};

/**
 * @summary Get Object Transport Candidates
 */
export const getObjectTransportCandidates = (
  params?: GetObjectTransportCandidatesParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLObjectTransportCandidatesResponse>(
    { url: `/project-explorer/object-transport-candidates`, method: 'GET', params, signal },
    options,
  );
};

export const getGetObjectTransportCandidatesQueryKey = (params?: GetObjectTransportCandidatesParams) => {
  return ['project-explorer', 'object-transport-candidates', ...(params ? [params] : [])] as const;
};

export const getGetObjectTransportCandidatesQueryOptions = <
  TData = Awaited<ReturnType<typeof getObjectTransportCandidates>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetObjectTransportCandidatesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectTransportCandidates>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetObjectTransportCandidatesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getObjectTransportCandidates>>> = ({ signal }) =>
    getObjectTransportCandidates(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getObjectTransportCandidates>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetObjectTransportCandidatesQueryResult = NonNullable<
  Awaited<ReturnType<typeof getObjectTransportCandidates>>
>;
export type GetObjectTransportCandidatesQueryError = ErrorType<TLHTTPValidationError>;

export function useGetObjectTransportCandidates<
  TData = Awaited<ReturnType<typeof getObjectTransportCandidates>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | GetObjectTransportCandidatesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectTransportCandidates>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getObjectTransportCandidates>>,
          TError,
          Awaited<ReturnType<typeof getObjectTransportCandidates>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetObjectTransportCandidates<
  TData = Awaited<ReturnType<typeof getObjectTransportCandidates>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetObjectTransportCandidatesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectTransportCandidates>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getObjectTransportCandidates>>,
          TError,
          Awaited<ReturnType<typeof getObjectTransportCandidates>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetObjectTransportCandidates<
  TData = Awaited<ReturnType<typeof getObjectTransportCandidates>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetObjectTransportCandidatesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectTransportCandidates>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Object Transport Candidates
 */

export function useGetObjectTransportCandidates<
  TData = Awaited<ReturnType<typeof getObjectTransportCandidates>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetObjectTransportCandidatesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectTransportCandidates>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetObjectTransportCandidatesQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Object Transport Candidates
 */
export const prefetchGetObjectTransportCandidatesQuery = async <
  TData = Awaited<ReturnType<typeof getObjectTransportCandidates>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: GetObjectTransportCandidatesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectTransportCandidates>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetObjectTransportCandidatesQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Object Transport Candidates
 */
export const invalidateGetObjectTransportCandidates = async (
  queryClient: QueryClient,
  params?: GetObjectTransportCandidatesParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetObjectTransportCandidatesQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Create Object
 */
export const createObject = (
  tLObjectCreateRequest: BodyType<TLObjectCreateRequest>,
  params?: CreateObjectParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLObjectCreateResponse>(
    {
      url: `/project-explorer/objects`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLObjectCreateRequest,
      params,
      signal,
    },
    options,
  );
};

export const getCreateObjectQueryKey = (
  tLObjectCreateRequest?: BodyType<TLObjectCreateRequest>,
  params?: CreateObjectParams,
) => {
  return ['POST', 'project-explorer', 'objects', ...(params ? [params] : []), tLObjectCreateRequest] as const;
};

export const getCreateObjectQueryOptions = <
  TData = Awaited<ReturnType<typeof createObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLObjectCreateRequest: BodyType<TLObjectCreateRequest>,
  params?: CreateObjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createObject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateObjectQueryKey(tLObjectCreateRequest, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createObject>>> = ({ signal }) =>
    createObject(tLObjectCreateRequest, params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createObject>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateObjectQueryResult = NonNullable<Awaited<ReturnType<typeof createObject>>>;
export type CreateObjectQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateObject<
  TData = Awaited<ReturnType<typeof createObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLObjectCreateRequest: BodyType<TLObjectCreateRequest>,
  params: undefined | CreateObjectParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createObject>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createObject>>,
          TError,
          Awaited<ReturnType<typeof createObject>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateObject<
  TData = Awaited<ReturnType<typeof createObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLObjectCreateRequest: BodyType<TLObjectCreateRequest>,
  params?: CreateObjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createObject>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createObject>>,
          TError,
          Awaited<ReturnType<typeof createObject>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateObject<
  TData = Awaited<ReturnType<typeof createObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLObjectCreateRequest: BodyType<TLObjectCreateRequest>,
  params?: CreateObjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createObject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Object
 */

export function useCreateObject<
  TData = Awaited<ReturnType<typeof createObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLObjectCreateRequest: BodyType<TLObjectCreateRequest>,
  params?: CreateObjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createObject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateObjectQueryOptions(tLObjectCreateRequest, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Object
 */
export const prefetchCreateObjectQuery = async <
  TData = Awaited<ReturnType<typeof createObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLObjectCreateRequest: BodyType<TLObjectCreateRequest>,
  params?: CreateObjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createObject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateObjectQueryOptions(tLObjectCreateRequest, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Object
 */
export const invalidateCreateObject = async (
  queryClient: QueryClient,
  tLObjectCreateRequest: BodyType<TLObjectCreateRequest>,
  params?: CreateObjectParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCreateObjectQueryKey(tLObjectCreateRequest, params) }, options);

  return queryClient;
};

/**
 * @summary Activate Object
 */
export const activateObject = (
  { objectUri }: ActivateObjectPathParameters,
  params?: ActivateObjectParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLObjectActivateResponse>(
    { url: `/project-explorer/objects/${objectUri}/activate`, method: 'POST', params, signal },
    options,
  );
};

export const getActivateObjectQueryKey = (
  { objectUri }: ActivateObjectPathParameters,
  params?: ActivateObjectParams,
) => {
  return ['POST', 'project-explorer', 'objects', objectUri, 'activate', ...(params ? [params] : [])] as const;
};

export const getActivateObjectQueryOptions = <
  TData = Awaited<ReturnType<typeof activateObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectUri }: ActivateObjectPathParameters,
  params?: ActivateObjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof activateObject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getActivateObjectQueryKey({ objectUri }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof activateObject>>> = ({ signal }) =>
    activateObject({ objectUri }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: objectUri !== null && objectUri !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof activateObject>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ActivateObjectQueryResult = NonNullable<Awaited<ReturnType<typeof activateObject>>>;
export type ActivateObjectQueryError = ErrorType<TLHTTPValidationError>;

export function useActivateObject<
  TData = Awaited<ReturnType<typeof activateObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ActivateObjectPathParameters,
  params: undefined | ActivateObjectParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof activateObject>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof activateObject>>,
          TError,
          Awaited<ReturnType<typeof activateObject>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useActivateObject<
  TData = Awaited<ReturnType<typeof activateObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ActivateObjectPathParameters,
  params?: ActivateObjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof activateObject>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof activateObject>>,
          TError,
          Awaited<ReturnType<typeof activateObject>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useActivateObject<
  TData = Awaited<ReturnType<typeof activateObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ActivateObjectPathParameters,
  params?: ActivateObjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof activateObject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Activate Object
 */

export function useActivateObject<
  TData = Awaited<ReturnType<typeof activateObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectUri }: ActivateObjectPathParameters,
  params?: ActivateObjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof activateObject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getActivateObjectQueryOptions({ objectUri }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Activate Object
 */
export const prefetchActivateObjectQuery = async <
  TData = Awaited<ReturnType<typeof activateObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { objectUri }: ActivateObjectPathParameters,
  params?: ActivateObjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof activateObject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getActivateObjectQueryOptions({ objectUri }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Activate Object
 */
export const invalidateActivateObject = async (
  queryClient: QueryClient,
  { objectUri }: ActivateObjectPathParameters,
  params?: ActivateObjectParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getActivateObjectQueryKey({ objectUri }, params) }, options);

  return queryClient;
};

/**
 * @summary Get Object Children
 */
export const getObjectChildren = (
  { objectUri }: GetObjectChildrenPathParameters,
  params?: GetObjectChildrenParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLObjectChildrenResponse>(
    { url: `/project-explorer/objects/${objectUri}/children`, method: 'GET', params, signal },
    options,
  );
};

export const getGetObjectChildrenQueryKey = (
  { objectUri }: GetObjectChildrenPathParameters,
  params?: GetObjectChildrenParams,
) => {
  return ['project-explorer', 'objects', objectUri, 'children', ...(params ? [params] : [])] as const;
};

export const getGetObjectChildrenQueryOptions = <
  TData = Awaited<ReturnType<typeof getObjectChildren>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectUri }: GetObjectChildrenPathParameters,
  params?: GetObjectChildrenParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectChildren>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetObjectChildrenQueryKey({ objectUri }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getObjectChildren>>> = ({ signal }) =>
    getObjectChildren({ objectUri }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: objectUri !== null && objectUri !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getObjectChildren>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetObjectChildrenQueryResult = NonNullable<Awaited<ReturnType<typeof getObjectChildren>>>;
export type GetObjectChildrenQueryError = ErrorType<TLHTTPValidationError>;

export function useGetObjectChildren<
  TData = Awaited<ReturnType<typeof getObjectChildren>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetObjectChildrenPathParameters,
  params: undefined | GetObjectChildrenParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectChildren>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getObjectChildren>>,
          TError,
          Awaited<ReturnType<typeof getObjectChildren>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetObjectChildren<
  TData = Awaited<ReturnType<typeof getObjectChildren>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetObjectChildrenPathParameters,
  params?: GetObjectChildrenParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectChildren>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getObjectChildren>>,
          TError,
          Awaited<ReturnType<typeof getObjectChildren>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetObjectChildren<
  TData = Awaited<ReturnType<typeof getObjectChildren>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetObjectChildrenPathParameters,
  params?: GetObjectChildrenParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectChildren>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Object Children
 */

export function useGetObjectChildren<
  TData = Awaited<ReturnType<typeof getObjectChildren>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectUri }: GetObjectChildrenPathParameters,
  params?: GetObjectChildrenParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectChildren>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetObjectChildrenQueryOptions({ objectUri }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Object Children
 */
export const prefetchGetObjectChildrenQuery = async <
  TData = Awaited<ReturnType<typeof getObjectChildren>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { objectUri }: GetObjectChildrenPathParameters,
  params?: GetObjectChildrenParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectChildren>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetObjectChildrenQueryOptions({ objectUri }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Object Children
 */
export const invalidateGetObjectChildren = async (
  queryClient: QueryClient,
  { objectUri }: GetObjectChildrenPathParameters,
  params?: GetObjectChildrenParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetObjectChildrenQueryKey({ objectUri }, params) }, options);

  return queryClient;
};

/**
 * @summary Get Object Metadata
 */
export const getObjectMetadata = (
  { objectUri }: GetObjectMetadataPathParameters,
  params?: GetObjectMetadataParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLObjectMetadataResponse>(
    { url: `/project-explorer/objects/${objectUri}/metadata`, method: 'GET', params, signal },
    options,
  );
};

export const getGetObjectMetadataQueryKey = (
  { objectUri }: GetObjectMetadataPathParameters,
  params?: GetObjectMetadataParams,
) => {
  return ['project-explorer', 'objects', objectUri, 'metadata', ...(params ? [params] : [])] as const;
};

export const getGetObjectMetadataQueryOptions = <
  TData = Awaited<ReturnType<typeof getObjectMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectUri }: GetObjectMetadataPathParameters,
  params?: GetObjectMetadataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectMetadata>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetObjectMetadataQueryKey({ objectUri }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getObjectMetadata>>> = ({ signal }) =>
    getObjectMetadata({ objectUri }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: objectUri !== null && objectUri !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getObjectMetadata>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetObjectMetadataQueryResult = NonNullable<Awaited<ReturnType<typeof getObjectMetadata>>>;
export type GetObjectMetadataQueryError = ErrorType<TLHTTPValidationError>;

export function useGetObjectMetadata<
  TData = Awaited<ReturnType<typeof getObjectMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetObjectMetadataPathParameters,
  params: undefined | GetObjectMetadataParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectMetadata>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getObjectMetadata>>,
          TError,
          Awaited<ReturnType<typeof getObjectMetadata>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetObjectMetadata<
  TData = Awaited<ReturnType<typeof getObjectMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetObjectMetadataPathParameters,
  params?: GetObjectMetadataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectMetadata>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getObjectMetadata>>,
          TError,
          Awaited<ReturnType<typeof getObjectMetadata>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetObjectMetadata<
  TData = Awaited<ReturnType<typeof getObjectMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetObjectMetadataPathParameters,
  params?: GetObjectMetadataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectMetadata>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Object Metadata
 */

export function useGetObjectMetadata<
  TData = Awaited<ReturnType<typeof getObjectMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectUri }: GetObjectMetadataPathParameters,
  params?: GetObjectMetadataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectMetadata>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetObjectMetadataQueryOptions({ objectUri }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Object Metadata
 */
export const prefetchGetObjectMetadataQuery = async <
  TData = Awaited<ReturnType<typeof getObjectMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { objectUri }: GetObjectMetadataPathParameters,
  params?: GetObjectMetadataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectMetadata>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetObjectMetadataQueryOptions({ objectUri }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Object Metadata
 */
export const invalidateGetObjectMetadata = async (
  queryClient: QueryClient,
  { objectUri }: GetObjectMetadataPathParameters,
  params?: GetObjectMetadataParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetObjectMetadataQueryKey({ objectUri }, params) }, options);

  return queryClient;
};

/**
 * @summary Navigate From Source
 */
export const navigateFromSource = (
  { objectUri }: NavigateFromSourcePathParameters,
  tLNavigateRequest: BodyType<TLNavigateRequest>,
  params?: NavigateFromSourceParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNavigateResponse | void>(
    {
      url: `/project-explorer/objects/${objectUri}/navigate`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNavigateRequest,
      params,
      signal,
    },
    options,
  );
};

export const getNavigateFromSourceQueryKey = (
  { objectUri }: NavigateFromSourcePathParameters,
  tLNavigateRequest?: BodyType<TLNavigateRequest>,
  params?: NavigateFromSourceParams,
) => {
  return [
    'POST',
    'project-explorer',
    'objects',
    objectUri,
    'navigate',
    ...(params ? [params] : []),
    tLNavigateRequest,
  ] as const;
};

export const getNavigateFromSourceQueryOptions = <
  TData = Awaited<ReturnType<typeof navigateFromSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectUri }: NavigateFromSourcePathParameters,
  tLNavigateRequest: BodyType<TLNavigateRequest>,
  params?: NavigateFromSourceParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof navigateFromSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getNavigateFromSourceQueryKey({ objectUri }, tLNavigateRequest, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof navigateFromSource>>> = ({ signal }) =>
    navigateFromSource({ objectUri }, tLNavigateRequest, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: objectUri !== null && objectUri !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof navigateFromSource>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type NavigateFromSourceQueryResult = NonNullable<Awaited<ReturnType<typeof navigateFromSource>>>;
export type NavigateFromSourceQueryError = ErrorType<TLHTTPValidationError>;

export function useNavigateFromSource<
  TData = Awaited<ReturnType<typeof navigateFromSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: NavigateFromSourcePathParameters,
  tLNavigateRequest: BodyType<TLNavigateRequest>,
  params: undefined | NavigateFromSourceParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof navigateFromSource>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigateFromSource>>,
          TError,
          Awaited<ReturnType<typeof navigateFromSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useNavigateFromSource<
  TData = Awaited<ReturnType<typeof navigateFromSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: NavigateFromSourcePathParameters,
  tLNavigateRequest: BodyType<TLNavigateRequest>,
  params?: NavigateFromSourceParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof navigateFromSource>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof navigateFromSource>>,
          TError,
          Awaited<ReturnType<typeof navigateFromSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useNavigateFromSource<
  TData = Awaited<ReturnType<typeof navigateFromSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: NavigateFromSourcePathParameters,
  tLNavigateRequest: BodyType<TLNavigateRequest>,
  params?: NavigateFromSourceParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof navigateFromSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Navigate From Source
 */

export function useNavigateFromSource<
  TData = Awaited<ReturnType<typeof navigateFromSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectUri }: NavigateFromSourcePathParameters,
  tLNavigateRequest: BodyType<TLNavigateRequest>,
  params?: NavigateFromSourceParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof navigateFromSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getNavigateFromSourceQueryOptions({ objectUri }, tLNavigateRequest, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Navigate From Source
 */
export const prefetchNavigateFromSourceQuery = async <
  TData = Awaited<ReturnType<typeof navigateFromSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { objectUri }: NavigateFromSourcePathParameters,
  tLNavigateRequest: BodyType<TLNavigateRequest>,
  params?: NavigateFromSourceParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof navigateFromSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getNavigateFromSourceQueryOptions({ objectUri }, tLNavigateRequest, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Navigate From Source
 */
export const invalidateNavigateFromSource = async (
  queryClient: QueryClient,
  { objectUri }: NavigateFromSourcePathParameters,
  tLNavigateRequest: BodyType<TLNavigateRequest>,
  params?: NavigateFromSourceParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getNavigateFromSourceQueryKey({ objectUri }, tLNavigateRequest, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Pretty Print Object
 */
export const prettyPrintObject = (
  { objectUri }: PrettyPrintObjectPathParameters,
  tLPrettyPrintRequest: BodyType<TLPrettyPrintRequest>,
  params?: PrettyPrintObjectParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPrettyPrintResponse>(
    {
      url: `/project-explorer/objects/${objectUri}/pretty-print`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLPrettyPrintRequest,
      params,
      signal,
    },
    options,
  );
};

export const getPrettyPrintObjectQueryKey = (
  { objectUri }: PrettyPrintObjectPathParameters,
  tLPrettyPrintRequest?: BodyType<TLPrettyPrintRequest>,
  params?: PrettyPrintObjectParams,
) => {
  return [
    'POST',
    'project-explorer',
    'objects',
    objectUri,
    'pretty-print',
    ...(params ? [params] : []),
    tLPrettyPrintRequest,
  ] as const;
};

export const getPrettyPrintObjectQueryOptions = <
  TData = Awaited<ReturnType<typeof prettyPrintObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectUri }: PrettyPrintObjectPathParameters,
  tLPrettyPrintRequest: BodyType<TLPrettyPrintRequest>,
  params?: PrettyPrintObjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof prettyPrintObject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getPrettyPrintObjectQueryKey({ objectUri }, tLPrettyPrintRequest, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof prettyPrintObject>>> = ({ signal }) =>
    prettyPrintObject({ objectUri }, tLPrettyPrintRequest, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: objectUri !== null && objectUri !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof prettyPrintObject>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type PrettyPrintObjectQueryResult = NonNullable<Awaited<ReturnType<typeof prettyPrintObject>>>;
export type PrettyPrintObjectQueryError = ErrorType<TLHTTPValidationError>;

export function usePrettyPrintObject<
  TData = Awaited<ReturnType<typeof prettyPrintObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PrettyPrintObjectPathParameters,
  tLPrettyPrintRequest: BodyType<TLPrettyPrintRequest>,
  params: undefined | PrettyPrintObjectParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof prettyPrintObject>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof prettyPrintObject>>,
          TError,
          Awaited<ReturnType<typeof prettyPrintObject>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePrettyPrintObject<
  TData = Awaited<ReturnType<typeof prettyPrintObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PrettyPrintObjectPathParameters,
  tLPrettyPrintRequest: BodyType<TLPrettyPrintRequest>,
  params?: PrettyPrintObjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof prettyPrintObject>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof prettyPrintObject>>,
          TError,
          Awaited<ReturnType<typeof prettyPrintObject>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePrettyPrintObject<
  TData = Awaited<ReturnType<typeof prettyPrintObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PrettyPrintObjectPathParameters,
  tLPrettyPrintRequest: BodyType<TLPrettyPrintRequest>,
  params?: PrettyPrintObjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof prettyPrintObject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Pretty Print Object
 */

export function usePrettyPrintObject<
  TData = Awaited<ReturnType<typeof prettyPrintObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectUri }: PrettyPrintObjectPathParameters,
  tLPrettyPrintRequest: BodyType<TLPrettyPrintRequest>,
  params?: PrettyPrintObjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof prettyPrintObject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getPrettyPrintObjectQueryOptions({ objectUri }, tLPrettyPrintRequest, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Pretty Print Object
 */
export const prefetchPrettyPrintObjectQuery = async <
  TData = Awaited<ReturnType<typeof prettyPrintObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { objectUri }: PrettyPrintObjectPathParameters,
  tLPrettyPrintRequest: BodyType<TLPrettyPrintRequest>,
  params?: PrettyPrintObjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof prettyPrintObject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getPrettyPrintObjectQueryOptions({ objectUri }, tLPrettyPrintRequest, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Pretty Print Object
 */
export const invalidatePrettyPrintObject = async (
  queryClient: QueryClient,
  { objectUri }: PrettyPrintObjectPathParameters,
  tLPrettyPrintRequest: BodyType<TLPrettyPrintRequest>,
  params?: PrettyPrintObjectParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getPrettyPrintObjectQueryKey({ objectUri }, tLPrettyPrintRequest, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Object Source
 */
export const getObjectSource = (
  { objectUri }: GetObjectSourcePathParameters,
  params?: GetObjectSourceParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLObjectSourceResponse>(
    { url: `/project-explorer/objects/${objectUri}/source`, method: 'GET', params, signal },
    options,
  );
};

export const getGetObjectSourceQueryKey = (
  { objectUri }: GetObjectSourcePathParameters,
  params?: GetObjectSourceParams,
) => {
  return ['project-explorer', 'objects', objectUri, 'source', ...(params ? [params] : [])] as const;
};

export const getGetObjectSourceQueryOptions = <
  TData = Awaited<ReturnType<typeof getObjectSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectUri }: GetObjectSourcePathParameters,
  params?: GetObjectSourceParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetObjectSourceQueryKey({ objectUri }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getObjectSource>>> = ({ signal }) =>
    getObjectSource({ objectUri }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: objectUri !== null && objectUri !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getObjectSource>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetObjectSourceQueryResult = NonNullable<Awaited<ReturnType<typeof getObjectSource>>>;
export type GetObjectSourceQueryError = ErrorType<TLHTTPValidationError>;

export function useGetObjectSource<
  TData = Awaited<ReturnType<typeof getObjectSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetObjectSourcePathParameters,
  params: undefined | GetObjectSourceParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectSource>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getObjectSource>>,
          TError,
          Awaited<ReturnType<typeof getObjectSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetObjectSource<
  TData = Awaited<ReturnType<typeof getObjectSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetObjectSourcePathParameters,
  params?: GetObjectSourceParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectSource>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getObjectSource>>,
          TError,
          Awaited<ReturnType<typeof getObjectSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetObjectSource<
  TData = Awaited<ReturnType<typeof getObjectSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetObjectSourcePathParameters,
  params?: GetObjectSourceParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Object Source
 */

export function useGetObjectSource<
  TData = Awaited<ReturnType<typeof getObjectSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectUri }: GetObjectSourcePathParameters,
  params?: GetObjectSourceParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetObjectSourceQueryOptions({ objectUri }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Object Source
 */
export const prefetchGetObjectSourceQuery = async <
  TData = Awaited<ReturnType<typeof getObjectSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { objectUri }: GetObjectSourcePathParameters,
  params?: GetObjectSourceParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetObjectSourceQueryOptions({ objectUri }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Object Source
 */
export const invalidateGetObjectSource = async (
  queryClient: QueryClient,
  { objectUri }: GetObjectSourcePathParameters,
  params?: GetObjectSourceParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetObjectSourceQueryKey({ objectUri }, params) }, options);

  return queryClient;
};

/**
 * @summary Save Object Source
 */
export const saveObjectSource = (
  { objectUri }: SaveObjectSourcePathParameters,
  tLObjectSourceUpdateRequest: BodyType<TLObjectSourceUpdateRequest>,
  params?: SaveObjectSourceParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLObjectSourceUpdateResponse>(
    {
      url: `/project-explorer/objects/${objectUri}/source`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: tLObjectSourceUpdateRequest,
      params,
      signal,
    },
    options,
  );
};

export const getSaveObjectSourceQueryKey = (
  { objectUri }: SaveObjectSourcePathParameters,
  tLObjectSourceUpdateRequest?: BodyType<TLObjectSourceUpdateRequest>,
  params?: SaveObjectSourceParams,
) => {
  return [
    'PUT',
    'project-explorer',
    'objects',
    objectUri,
    'source',
    ...(params ? [params] : []),
    tLObjectSourceUpdateRequest,
  ] as const;
};

export const getSaveObjectSourceQueryOptions = <
  TData = Awaited<ReturnType<typeof saveObjectSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectUri }: SaveObjectSourcePathParameters,
  tLObjectSourceUpdateRequest: BodyType<TLObjectSourceUpdateRequest>,
  params?: SaveObjectSourceParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof saveObjectSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getSaveObjectSourceQueryKey({ objectUri }, tLObjectSourceUpdateRequest, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof saveObjectSource>>> = ({ signal }) =>
    saveObjectSource({ objectUri }, tLObjectSourceUpdateRequest, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: objectUri !== null && objectUri !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof saveObjectSource>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type SaveObjectSourceQueryResult = NonNullable<Awaited<ReturnType<typeof saveObjectSource>>>;
export type SaveObjectSourceQueryError = ErrorType<TLHTTPValidationError>;

export function useSaveObjectSource<
  TData = Awaited<ReturnType<typeof saveObjectSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SaveObjectSourcePathParameters,
  tLObjectSourceUpdateRequest: BodyType<TLObjectSourceUpdateRequest>,
  params: undefined | SaveObjectSourceParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof saveObjectSource>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof saveObjectSource>>,
          TError,
          Awaited<ReturnType<typeof saveObjectSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSaveObjectSource<
  TData = Awaited<ReturnType<typeof saveObjectSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SaveObjectSourcePathParameters,
  tLObjectSourceUpdateRequest: BodyType<TLObjectSourceUpdateRequest>,
  params?: SaveObjectSourceParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof saveObjectSource>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof saveObjectSource>>,
          TError,
          Awaited<ReturnType<typeof saveObjectSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSaveObjectSource<
  TData = Awaited<ReturnType<typeof saveObjectSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SaveObjectSourcePathParameters,
  tLObjectSourceUpdateRequest: BodyType<TLObjectSourceUpdateRequest>,
  params?: SaveObjectSourceParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof saveObjectSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Save Object Source
 */

export function useSaveObjectSource<
  TData = Awaited<ReturnType<typeof saveObjectSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectUri }: SaveObjectSourcePathParameters,
  tLObjectSourceUpdateRequest: BodyType<TLObjectSourceUpdateRequest>,
  params?: SaveObjectSourceParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof saveObjectSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSaveObjectSourceQueryOptions({ objectUri }, tLObjectSourceUpdateRequest, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Save Object Source
 */
export const prefetchSaveObjectSourceQuery = async <
  TData = Awaited<ReturnType<typeof saveObjectSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { objectUri }: SaveObjectSourcePathParameters,
  tLObjectSourceUpdateRequest: BodyType<TLObjectSourceUpdateRequest>,
  params?: SaveObjectSourceParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof saveObjectSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSaveObjectSourceQueryOptions({ objectUri }, tLObjectSourceUpdateRequest, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Save Object Source
 */
export const invalidateSaveObjectSource = async (
  queryClient: QueryClient,
  { objectUri }: SaveObjectSourcePathParameters,
  tLObjectSourceUpdateRequest: BodyType<TLObjectSourceUpdateRequest>,
  params?: SaveObjectSourceParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getSaveObjectSourceQueryKey({ objectUri }, tLObjectSourceUpdateRequest, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Syntax Check Object
 */
export const syntaxCheckObject = (
  { objectUri }: SyntaxCheckObjectPathParameters,
  tLProjectExplorerSyntaxCheckRequest: BodyType<TLProjectExplorerSyntaxCheckRequest>,
  params?: SyntaxCheckObjectParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLProjectExplorerSyntaxCheckResponse>(
    {
      url: `/project-explorer/objects/${objectUri}/syntax-check`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLProjectExplorerSyntaxCheckRequest,
      params,
      signal,
    },
    options,
  );
};

export const getSyntaxCheckObjectQueryKey = (
  { objectUri }: SyntaxCheckObjectPathParameters,
  tLProjectExplorerSyntaxCheckRequest?: BodyType<TLProjectExplorerSyntaxCheckRequest>,
  params?: SyntaxCheckObjectParams,
) => {
  return [
    'POST',
    'project-explorer',
    'objects',
    objectUri,
    'syntax-check',
    ...(params ? [params] : []),
    tLProjectExplorerSyntaxCheckRequest,
  ] as const;
};

export const getSyntaxCheckObjectQueryOptions = <
  TData = Awaited<ReturnType<typeof syntaxCheckObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectUri }: SyntaxCheckObjectPathParameters,
  tLProjectExplorerSyntaxCheckRequest: BodyType<TLProjectExplorerSyntaxCheckRequest>,
  params?: SyntaxCheckObjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof syntaxCheckObject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getSyntaxCheckObjectQueryKey({ objectUri }, tLProjectExplorerSyntaxCheckRequest, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof syntaxCheckObject>>> = ({ signal }) =>
    syntaxCheckObject({ objectUri }, tLProjectExplorerSyntaxCheckRequest, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: objectUri !== null && objectUri !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof syntaxCheckObject>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type SyntaxCheckObjectQueryResult = NonNullable<Awaited<ReturnType<typeof syntaxCheckObject>>>;
export type SyntaxCheckObjectQueryError = ErrorType<TLHTTPValidationError>;

export function useSyntaxCheckObject<
  TData = Awaited<ReturnType<typeof syntaxCheckObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SyntaxCheckObjectPathParameters,
  tLProjectExplorerSyntaxCheckRequest: BodyType<TLProjectExplorerSyntaxCheckRequest>,
  params: undefined | SyntaxCheckObjectParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof syntaxCheckObject>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof syntaxCheckObject>>,
          TError,
          Awaited<ReturnType<typeof syntaxCheckObject>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSyntaxCheckObject<
  TData = Awaited<ReturnType<typeof syntaxCheckObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SyntaxCheckObjectPathParameters,
  tLProjectExplorerSyntaxCheckRequest: BodyType<TLProjectExplorerSyntaxCheckRequest>,
  params?: SyntaxCheckObjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof syntaxCheckObject>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof syntaxCheckObject>>,
          TError,
          Awaited<ReturnType<typeof syntaxCheckObject>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSyntaxCheckObject<
  TData = Awaited<ReturnType<typeof syntaxCheckObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SyntaxCheckObjectPathParameters,
  tLProjectExplorerSyntaxCheckRequest: BodyType<TLProjectExplorerSyntaxCheckRequest>,
  params?: SyntaxCheckObjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof syntaxCheckObject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Syntax Check Object
 */

export function useSyntaxCheckObject<
  TData = Awaited<ReturnType<typeof syntaxCheckObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectUri }: SyntaxCheckObjectPathParameters,
  tLProjectExplorerSyntaxCheckRequest: BodyType<TLProjectExplorerSyntaxCheckRequest>,
  params?: SyntaxCheckObjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof syntaxCheckObject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSyntaxCheckObjectQueryOptions(
    { objectUri },
    tLProjectExplorerSyntaxCheckRequest,
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Syntax Check Object
 */
export const prefetchSyntaxCheckObjectQuery = async <
  TData = Awaited<ReturnType<typeof syntaxCheckObject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { objectUri }: SyntaxCheckObjectPathParameters,
  tLProjectExplorerSyntaxCheckRequest: BodyType<TLProjectExplorerSyntaxCheckRequest>,
  params?: SyntaxCheckObjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof syntaxCheckObject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSyntaxCheckObjectQueryOptions(
    { objectUri },
    tLProjectExplorerSyntaxCheckRequest,
    params,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Syntax Check Object
 */
export const invalidateSyntaxCheckObject = async (
  queryClient: QueryClient,
  { objectUri }: SyntaxCheckObjectPathParameters,
  tLProjectExplorerSyntaxCheckRequest: BodyType<TLProjectExplorerSyntaxCheckRequest>,
  params?: SyntaxCheckObjectParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getSyntaxCheckObjectQueryKey({ objectUri }, tLProjectExplorerSyntaxCheckRequest, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Table Data Preview
 */
export const getTableDataPreview = (
  { objectUri }: GetTableDataPreviewPathParameters,
  params?: GetTableDataPreviewParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLTableDataPreviewResponse>(
    { url: `/project-explorer/objects/${objectUri}/table-preview`, method: 'POST', params, signal },
    options,
  );
};

export const getGetTableDataPreviewQueryKey = (
  { objectUri }: GetTableDataPreviewPathParameters,
  params?: GetTableDataPreviewParams,
) => {
  return ['POST', 'project-explorer', 'objects', objectUri, 'table-preview', ...(params ? [params] : [])] as const;
};

export const getGetTableDataPreviewQueryOptions = <
  TData = Awaited<ReturnType<typeof getTableDataPreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectUri }: GetTableDataPreviewPathParameters,
  params?: GetTableDataPreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTableDataPreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTableDataPreviewQueryKey({ objectUri }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTableDataPreview>>> = ({ signal }) =>
    getTableDataPreview({ objectUri }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: objectUri !== null && objectUri !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getTableDataPreview>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetTableDataPreviewQueryResult = NonNullable<Awaited<ReturnType<typeof getTableDataPreview>>>;
export type GetTableDataPreviewQueryError = ErrorType<TLHTTPValidationError>;

export function useGetTableDataPreview<
  TData = Awaited<ReturnType<typeof getTableDataPreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetTableDataPreviewPathParameters,
  params: undefined | GetTableDataPreviewParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTableDataPreview>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTableDataPreview>>,
          TError,
          Awaited<ReturnType<typeof getTableDataPreview>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTableDataPreview<
  TData = Awaited<ReturnType<typeof getTableDataPreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetTableDataPreviewPathParameters,
  params?: GetTableDataPreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTableDataPreview>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTableDataPreview>>,
          TError,
          Awaited<ReturnType<typeof getTableDataPreview>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTableDataPreview<
  TData = Awaited<ReturnType<typeof getTableDataPreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetTableDataPreviewPathParameters,
  params?: GetTableDataPreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTableDataPreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Table Data Preview
 */

export function useGetTableDataPreview<
  TData = Awaited<ReturnType<typeof getTableDataPreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectUri }: GetTableDataPreviewPathParameters,
  params?: GetTableDataPreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTableDataPreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetTableDataPreviewQueryOptions({ objectUri }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Table Data Preview
 */
export const prefetchGetTableDataPreviewQuery = async <
  TData = Awaited<ReturnType<typeof getTableDataPreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { objectUri }: GetTableDataPreviewPathParameters,
  params?: GetTableDataPreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTableDataPreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetTableDataPreviewQueryOptions({ objectUri }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Table Data Preview
 */
export const invalidateGetTableDataPreview = async (
  queryClient: QueryClient,
  { objectUri }: GetTableDataPreviewPathParameters,
  params?: GetTableDataPreviewParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetTableDataPreviewQueryKey({ objectUri }, params) }, options);

  return queryClient;
};

/**
 * @summary Get Object Transport Info
 */
export const getObjectTransportInfo = (
  { objectUri }: GetObjectTransportInfoPathParameters,
  params?: GetObjectTransportInfoParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLObjectTransportInfoResponse>(
    { url: `/project-explorer/objects/${objectUri}/transport-info`, method: 'GET', params, signal },
    options,
  );
};

export const getGetObjectTransportInfoQueryKey = (
  { objectUri }: GetObjectTransportInfoPathParameters,
  params?: GetObjectTransportInfoParams,
) => {
  return ['project-explorer', 'objects', objectUri, 'transport-info', ...(params ? [params] : [])] as const;
};

export const getGetObjectTransportInfoQueryOptions = <
  TData = Awaited<ReturnType<typeof getObjectTransportInfo>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectUri }: GetObjectTransportInfoPathParameters,
  params?: GetObjectTransportInfoParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectTransportInfo>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetObjectTransportInfoQueryKey({ objectUri }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getObjectTransportInfo>>> = ({ signal }) =>
    getObjectTransportInfo({ objectUri }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: objectUri !== null && objectUri !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getObjectTransportInfo>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetObjectTransportInfoQueryResult = NonNullable<Awaited<ReturnType<typeof getObjectTransportInfo>>>;
export type GetObjectTransportInfoQueryError = ErrorType<TLHTTPValidationError>;

export function useGetObjectTransportInfo<
  TData = Awaited<ReturnType<typeof getObjectTransportInfo>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetObjectTransportInfoPathParameters,
  params: undefined | GetObjectTransportInfoParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectTransportInfo>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getObjectTransportInfo>>,
          TError,
          Awaited<ReturnType<typeof getObjectTransportInfo>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetObjectTransportInfo<
  TData = Awaited<ReturnType<typeof getObjectTransportInfo>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetObjectTransportInfoPathParameters,
  params?: GetObjectTransportInfoParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectTransportInfo>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getObjectTransportInfo>>,
          TError,
          Awaited<ReturnType<typeof getObjectTransportInfo>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetObjectTransportInfo<
  TData = Awaited<ReturnType<typeof getObjectTransportInfo>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetObjectTransportInfoPathParameters,
  params?: GetObjectTransportInfoParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectTransportInfo>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Object Transport Info
 */

export function useGetObjectTransportInfo<
  TData = Awaited<ReturnType<typeof getObjectTransportInfo>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectUri }: GetObjectTransportInfoPathParameters,
  params?: GetObjectTransportInfoParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectTransportInfo>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetObjectTransportInfoQueryOptions({ objectUri }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Object Transport Info
 */
export const prefetchGetObjectTransportInfoQuery = async <
  TData = Awaited<ReturnType<typeof getObjectTransportInfo>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { objectUri }: GetObjectTransportInfoPathParameters,
  params?: GetObjectTransportInfoParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getObjectTransportInfo>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetObjectTransportInfoQueryOptions({ objectUri }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Object Transport Info
 */
export const invalidateGetObjectTransportInfo = async (
  queryClient: QueryClient,
  { objectUri }: GetObjectTransportInfoPathParameters,
  params?: GetObjectTransportInfoParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetObjectTransportInfoQueryKey({ objectUri }, params) }, options);

  return queryClient;
};

/**
 * @summary Get Usage References
 */
export const getUsageReferences = (
  { objectUri }: GetUsageReferencesPathParameters,
  stringNull?: BodyType<string[] | null> | null,
  params?: GetUsageReferencesParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLUsageReferencesResponse>(
    {
      url: `/project-explorer/objects/${objectUri}/usage-references`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      params,
      signal,
    },
    options,
  );
};

export const getGetUsageReferencesQueryKey = (
  { objectUri }: GetUsageReferencesPathParameters,
  stringNull?: BodyType<string[] | null>,
  params?: GetUsageReferencesParams,
) => {
  return [
    'project-explorer',
    'objects',
    objectUri,
    'usage-references',
    ...(params ? [params] : []),
    stringNull,
  ] as const;
};

export const getGetUsageReferencesQueryOptions = <
  TData = Awaited<ReturnType<typeof getUsageReferences>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectUri }: GetUsageReferencesPathParameters,
  stringNull?: BodyType<string[] | null>,
  params?: GetUsageReferencesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUsageReferences>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUsageReferencesQueryKey({ objectUri }, stringNull, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUsageReferences>>> = ({ signal }) =>
    getUsageReferences({ objectUri }, stringNull, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: objectUri !== null && objectUri !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getUsageReferences>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetUsageReferencesQueryResult = NonNullable<Awaited<ReturnType<typeof getUsageReferences>>>;
export type GetUsageReferencesQueryError = ErrorType<TLHTTPValidationError>;

export function useGetUsageReferences<
  TData = Awaited<ReturnType<typeof getUsageReferences>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetUsageReferencesPathParameters,
  stringNull: undefined | BodyType<string[] | null>,
  params: undefined | GetUsageReferencesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUsageReferences>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUsageReferences>>,
          TError,
          Awaited<ReturnType<typeof getUsageReferences>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUsageReferences<
  TData = Awaited<ReturnType<typeof getUsageReferences>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetUsageReferencesPathParameters,
  stringNull?: BodyType<string[] | null>,
  params?: GetUsageReferencesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUsageReferences>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUsageReferences>>,
          TError,
          Awaited<ReturnType<typeof getUsageReferences>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUsageReferences<
  TData = Awaited<ReturnType<typeof getUsageReferences>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetUsageReferencesPathParameters,
  stringNull?: BodyType<string[] | null>,
  params?: GetUsageReferencesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUsageReferences>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Usage References
 */

export function useGetUsageReferences<
  TData = Awaited<ReturnType<typeof getUsageReferences>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { objectUri }: GetUsageReferencesPathParameters,
  stringNull?: BodyType<string[] | null>,
  params?: GetUsageReferencesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUsageReferences>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetUsageReferencesQueryOptions({ objectUri }, stringNull, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Usage References
 */
export const prefetchGetUsageReferencesQuery = async <
  TData = Awaited<ReturnType<typeof getUsageReferences>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { objectUri }: GetUsageReferencesPathParameters,
  stringNull?: BodyType<string[] | null>,
  params?: GetUsageReferencesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUsageReferences>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetUsageReferencesQueryOptions({ objectUri }, stringNull, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Usage References
 */
export const invalidateGetUsageReferences = async (
  queryClient: QueryClient,
  { objectUri }: GetUsageReferencesPathParameters,
  stringNull?: BodyType<string[] | null>,
  params?: GetUsageReferencesParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getGetUsageReferencesQueryKey({ objectUri }, stringNull, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Search Packages
 */
export const searchPackages = (
  params: SearchPackagesParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPackageSearchResponse>(
    { url: `/project-explorer/packages`, method: 'GET', params, signal },
    options,
  );
};

export const getSearchPackagesQueryKey = (params?: SearchPackagesParams) => {
  return ['project-explorer', 'packages', ...(params ? [params] : [])] as const;
};

export const getSearchPackagesQueryOptions = <
  TData = Awaited<ReturnType<typeof searchPackages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: SearchPackagesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchPackages>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSearchPackagesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof searchPackages>>> = ({ signal }) =>
    searchPackages(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof searchPackages>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SearchPackagesQueryResult = NonNullable<Awaited<ReturnType<typeof searchPackages>>>;
export type SearchPackagesQueryError = ErrorType<TLHTTPValidationError>;

export function useSearchPackages<
  TData = Awaited<ReturnType<typeof searchPackages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: SearchPackagesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchPackages>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchPackages>>,
          TError,
          Awaited<ReturnType<typeof searchPackages>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchPackages<
  TData = Awaited<ReturnType<typeof searchPackages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: SearchPackagesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchPackages>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchPackages>>,
          TError,
          Awaited<ReturnType<typeof searchPackages>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchPackages<
  TData = Awaited<ReturnType<typeof searchPackages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: SearchPackagesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchPackages>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Search Packages
 */

export function useSearchPackages<
  TData = Awaited<ReturnType<typeof searchPackages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: SearchPackagesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchPackages>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSearchPackagesQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Search Packages
 */
export const prefetchSearchPackagesQuery = async <
  TData = Awaited<ReturnType<typeof searchPackages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params: SearchPackagesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchPackages>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSearchPackagesQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Search Packages
 */
export const invalidateSearchPackages = async (
  queryClient: QueryClient,
  params: SearchPackagesParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getSearchPackagesQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Expand Package
 */
export const expandPackage = (
  { packageId }: ExpandPackagePathParameters,
  params?: ExpandPackageParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLProjectExplorerTreeResponse>(
    { url: `/project-explorer/packages/${packageId}/children`, method: 'GET', params, signal },
    options,
  );
};

export const getExpandPackageQueryKey = ({ packageId }: ExpandPackagePathParameters, params?: ExpandPackageParams) => {
  return ['project-explorer', 'packages', packageId, 'children', ...(params ? [params] : [])] as const;
};

export const getExpandPackageQueryOptions = <
  TData = Awaited<ReturnType<typeof expandPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: ExpandPackagePathParameters,
  params?: ExpandPackageParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof expandPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getExpandPackageQueryKey({ packageId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof expandPackage>>> = ({ signal }) =>
    expandPackage({ packageId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: packageId !== null && packageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof expandPackage>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ExpandPackageQueryResult = NonNullable<Awaited<ReturnType<typeof expandPackage>>>;
export type ExpandPackageQueryError = ErrorType<TLHTTPValidationError>;

export function useExpandPackage<
  TData = Awaited<ReturnType<typeof expandPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExpandPackagePathParameters,
  params: undefined | ExpandPackageParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof expandPackage>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof expandPackage>>,
          TError,
          Awaited<ReturnType<typeof expandPackage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExpandPackage<
  TData = Awaited<ReturnType<typeof expandPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExpandPackagePathParameters,
  params?: ExpandPackageParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof expandPackage>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof expandPackage>>,
          TError,
          Awaited<ReturnType<typeof expandPackage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExpandPackage<
  TData = Awaited<ReturnType<typeof expandPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExpandPackagePathParameters,
  params?: ExpandPackageParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof expandPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Expand Package
 */

export function useExpandPackage<
  TData = Awaited<ReturnType<typeof expandPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: ExpandPackagePathParameters,
  params?: ExpandPackageParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof expandPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getExpandPackageQueryOptions({ packageId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Expand Package
 */
export const prefetchExpandPackageQuery = async <
  TData = Awaited<ReturnType<typeof expandPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { packageId }: ExpandPackagePathParameters,
  params?: ExpandPackageParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof expandPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getExpandPackageQueryOptions({ packageId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Expand Package
 */
export const invalidateExpandPackage = async (
  queryClient: QueryClient,
  { packageId }: ExpandPackagePathParameters,
  params?: ExpandPackageParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getExpandPackageQueryKey({ packageId }, params) }, options);

  return queryClient;
};

/**
 * @summary Get Pretty Print Settings
 */
export const getPrettyPrintSettings = (
  params?: GetPrettyPrintSettingsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPrettyPrintSettings>(
    { url: `/project-explorer/pretty-print-settings`, method: 'GET', params, signal },
    options,
  );
};

export const getGetPrettyPrintSettingsQueryKey = (params?: GetPrettyPrintSettingsParams) => {
  return ['project-explorer', 'pretty-print-settings', ...(params ? [params] : [])] as const;
};

export const getGetPrettyPrintSettingsQueryOptions = <
  TData = Awaited<ReturnType<typeof getPrettyPrintSettings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetPrettyPrintSettingsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPrettyPrintSettings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPrettyPrintSettingsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getPrettyPrintSettings>>> = ({ signal }) =>
    getPrettyPrintSettings(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getPrettyPrintSettings>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPrettyPrintSettingsQueryResult = NonNullable<Awaited<ReturnType<typeof getPrettyPrintSettings>>>;
export type GetPrettyPrintSettingsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetPrettyPrintSettings<
  TData = Awaited<ReturnType<typeof getPrettyPrintSettings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | GetPrettyPrintSettingsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPrettyPrintSettings>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPrettyPrintSettings>>,
          TError,
          Awaited<ReturnType<typeof getPrettyPrintSettings>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPrettyPrintSettings<
  TData = Awaited<ReturnType<typeof getPrettyPrintSettings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetPrettyPrintSettingsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPrettyPrintSettings>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPrettyPrintSettings>>,
          TError,
          Awaited<ReturnType<typeof getPrettyPrintSettings>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPrettyPrintSettings<
  TData = Awaited<ReturnType<typeof getPrettyPrintSettings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetPrettyPrintSettingsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPrettyPrintSettings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Pretty Print Settings
 */

export function useGetPrettyPrintSettings<
  TData = Awaited<ReturnType<typeof getPrettyPrintSettings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetPrettyPrintSettingsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPrettyPrintSettings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetPrettyPrintSettingsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Pretty Print Settings
 */
export const prefetchGetPrettyPrintSettingsQuery = async <
  TData = Awaited<ReturnType<typeof getPrettyPrintSettings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: GetPrettyPrintSettingsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPrettyPrintSettings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetPrettyPrintSettingsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Pretty Print Settings
 */
export const invalidateGetPrettyPrintSettings = async (
  queryClient: QueryClient,
  params?: GetPrettyPrintSettingsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetPrettyPrintSettingsQueryKey(params) }, options);

  return queryClient;
};

/**
 * Persist the user's Pretty Printer settings to SAP. See GET.
 * @summary Update Pretty Print Settings
 */
export const updatePrettyPrintSettings = (
  tLPrettyPrintSettings: BodyType<TLPrettyPrintSettings>,
  params?: UpdatePrettyPrintSettingsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPrettyPrintSettings>(
    {
      url: `/project-explorer/pretty-print-settings`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: tLPrettyPrintSettings,
      params,
      signal,
    },
    options,
  );
};

export const getUpdatePrettyPrintSettingsQueryKey = (
  tLPrettyPrintSettings?: BodyType<TLPrettyPrintSettings>,
  params?: UpdatePrettyPrintSettingsParams,
) => {
  return [
    'PUT',
    'project-explorer',
    'pretty-print-settings',
    ...(params ? [params] : []),
    tLPrettyPrintSettings,
  ] as const;
};

export const getUpdatePrettyPrintSettingsQueryOptions = <
  TData = Awaited<ReturnType<typeof updatePrettyPrintSettings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPrettyPrintSettings: BodyType<TLPrettyPrintSettings>,
  params?: UpdatePrettyPrintSettingsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updatePrettyPrintSettings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdatePrettyPrintSettingsQueryKey(tLPrettyPrintSettings, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updatePrettyPrintSettings>>> = ({ signal }) =>
    updatePrettyPrintSettings(tLPrettyPrintSettings, params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof updatePrettyPrintSettings>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type UpdatePrettyPrintSettingsQueryResult = NonNullable<Awaited<ReturnType<typeof updatePrettyPrintSettings>>>;
export type UpdatePrettyPrintSettingsQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdatePrettyPrintSettings<
  TData = Awaited<ReturnType<typeof updatePrettyPrintSettings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPrettyPrintSettings: BodyType<TLPrettyPrintSettings>,
  params: undefined | UpdatePrettyPrintSettingsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updatePrettyPrintSettings>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updatePrettyPrintSettings>>,
          TError,
          Awaited<ReturnType<typeof updatePrettyPrintSettings>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdatePrettyPrintSettings<
  TData = Awaited<ReturnType<typeof updatePrettyPrintSettings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPrettyPrintSettings: BodyType<TLPrettyPrintSettings>,
  params?: UpdatePrettyPrintSettingsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updatePrettyPrintSettings>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updatePrettyPrintSettings>>,
          TError,
          Awaited<ReturnType<typeof updatePrettyPrintSettings>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdatePrettyPrintSettings<
  TData = Awaited<ReturnType<typeof updatePrettyPrintSettings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPrettyPrintSettings: BodyType<TLPrettyPrintSettings>,
  params?: UpdatePrettyPrintSettingsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updatePrettyPrintSettings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Pretty Print Settings
 */

export function useUpdatePrettyPrintSettings<
  TData = Awaited<ReturnType<typeof updatePrettyPrintSettings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLPrettyPrintSettings: BodyType<TLPrettyPrintSettings>,
  params?: UpdatePrettyPrintSettingsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updatePrettyPrintSettings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdatePrettyPrintSettingsQueryOptions(tLPrettyPrintSettings, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Pretty Print Settings
 */
export const prefetchUpdatePrettyPrintSettingsQuery = async <
  TData = Awaited<ReturnType<typeof updatePrettyPrintSettings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLPrettyPrintSettings: BodyType<TLPrettyPrintSettings>,
  params?: UpdatePrettyPrintSettingsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updatePrettyPrintSettings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdatePrettyPrintSettingsQueryOptions(tLPrettyPrintSettings, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Pretty Print Settings
 */
export const invalidateUpdatePrettyPrintSettings = async (
  queryClient: QueryClient,
  tLPrettyPrintSettings: BodyType<TLPrettyPrintSettings>,
  params?: UpdatePrettyPrintSettingsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdatePrettyPrintSettingsQueryKey(tLPrettyPrintSettings, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary List Program Subobjects
 */
export const listProgramSubobjects = (
  { parentProgramId }: ListProgramSubobjectsPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSubobjectListResponse>(
    { url: `/project-explorer/programs/${parentProgramId}/subobjects`, method: 'GET', signal },
    options,
  );
};

export const getListProgramSubobjectsQueryKey = ({ parentProgramId }: ListProgramSubobjectsPathParameters) => {
  return ['project-explorer', 'programs', parentProgramId, 'subobjects'] as const;
};

export const getListProgramSubobjectsQueryOptions = <
  TData = Awaited<ReturnType<typeof listProgramSubobjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { parentProgramId }: ListProgramSubobjectsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listProgramSubobjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListProgramSubobjectsQueryKey({ parentProgramId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listProgramSubobjects>>> = ({ signal }) =>
    listProgramSubobjects({ parentProgramId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: parentProgramId !== null && parentProgramId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listProgramSubobjects>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListProgramSubobjectsQueryResult = NonNullable<Awaited<ReturnType<typeof listProgramSubobjects>>>;
export type ListProgramSubobjectsQueryError = ErrorType<TLHTTPValidationError>;

export function useListProgramSubobjects<
  TData = Awaited<ReturnType<typeof listProgramSubobjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListProgramSubobjectsPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listProgramSubobjects>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listProgramSubobjects>>,
          TError,
          Awaited<ReturnType<typeof listProgramSubobjects>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListProgramSubobjects<
  TData = Awaited<ReturnType<typeof listProgramSubobjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListProgramSubobjectsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listProgramSubobjects>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listProgramSubobjects>>,
          TError,
          Awaited<ReturnType<typeof listProgramSubobjects>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListProgramSubobjects<
  TData = Awaited<ReturnType<typeof listProgramSubobjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListProgramSubobjectsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listProgramSubobjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Program Subobjects
 */

export function useListProgramSubobjects<
  TData = Awaited<ReturnType<typeof listProgramSubobjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { parentProgramId }: ListProgramSubobjectsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listProgramSubobjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListProgramSubobjectsQueryOptions({ parentProgramId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Program Subobjects
 */
export const prefetchListProgramSubobjectsQuery = async <
  TData = Awaited<ReturnType<typeof listProgramSubobjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { parentProgramId }: ListProgramSubobjectsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listProgramSubobjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListProgramSubobjectsQueryOptions({ parentProgramId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Program Subobjects
 */
export const invalidateListProgramSubobjects = async (
  queryClient: QueryClient,
  { parentProgramId }: ListProgramSubobjectsPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListProgramSubobjectsQueryKey({ parentProgramId }) }, options);

  return queryClient;
};

/**
 * @summary Get Root Nodes
 */
export const getRootNodes = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLProjectExplorerTreeResponse>({ url: `/project-explorer/roots`, method: 'GET', signal }, options);
};

export const getGetRootNodesQueryKey = () => {
  return ['project-explorer', 'roots'] as const;
};

export const getGetRootNodesQueryOptions = <
  TData = Awaited<ReturnType<typeof getRootNodes>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRootNodes>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetRootNodesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getRootNodes>>> = ({ signal }) =>
    getRootNodes(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getRootNodes>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetRootNodesQueryResult = NonNullable<Awaited<ReturnType<typeof getRootNodes>>>;
export type GetRootNodesQueryError = ErrorType<unknown>;

export function useGetRootNodes<TData = Awaited<ReturnType<typeof getRootNodes>>, TError = ErrorType<unknown>>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRootNodes>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getRootNodes>>,
          TError,
          Awaited<ReturnType<typeof getRootNodes>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetRootNodes<TData = Awaited<ReturnType<typeof getRootNodes>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRootNodes>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getRootNodes>>,
          TError,
          Awaited<ReturnType<typeof getRootNodes>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetRootNodes<TData = Awaited<ReturnType<typeof getRootNodes>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRootNodes>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Root Nodes
 */

export function useGetRootNodes<TData = Awaited<ReturnType<typeof getRootNodes>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRootNodes>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetRootNodesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Root Nodes
 */
export const prefetchGetRootNodesQuery = async <
  TData = Awaited<ReturnType<typeof getRootNodes>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRootNodes>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetRootNodesQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Root Nodes
 */
export const invalidateGetRootNodes = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetRootNodesQueryKey() }, options);

  return queryClient;
};

/**
 * @summary Get Sap Gui Url
 */
export const getSapGuiUrl = (
  params: GetSapGuiUrlParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSapGuiLinkResponse>(
    { url: `/project-explorer/sap-gui-url`, method: 'GET', params, signal },
    options,
  );
};

export const getGetSapGuiUrlQueryKey = (params?: GetSapGuiUrlParams) => {
  return ['project-explorer', 'sap-gui-url', ...(params ? [params] : [])] as const;
};

export const getGetSapGuiUrlQueryOptions = <
  TData = Awaited<ReturnType<typeof getSapGuiUrl>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetSapGuiUrlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSapGuiUrl>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSapGuiUrlQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSapGuiUrl>>> = ({ signal }) =>
    getSapGuiUrl(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getSapGuiUrl>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetSapGuiUrlQueryResult = NonNullable<Awaited<ReturnType<typeof getSapGuiUrl>>>;
export type GetSapGuiUrlQueryError = ErrorType<TLHTTPValidationError>;

export function useGetSapGuiUrl<
  TData = Awaited<ReturnType<typeof getSapGuiUrl>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetSapGuiUrlParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSapGuiUrl>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSapGuiUrl>>,
          TError,
          Awaited<ReturnType<typeof getSapGuiUrl>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSapGuiUrl<
  TData = Awaited<ReturnType<typeof getSapGuiUrl>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetSapGuiUrlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSapGuiUrl>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSapGuiUrl>>,
          TError,
          Awaited<ReturnType<typeof getSapGuiUrl>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSapGuiUrl<
  TData = Awaited<ReturnType<typeof getSapGuiUrl>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetSapGuiUrlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSapGuiUrl>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Sap Gui Url
 */

export function useGetSapGuiUrl<
  TData = Awaited<ReturnType<typeof getSapGuiUrl>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetSapGuiUrlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSapGuiUrl>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetSapGuiUrlQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Sap Gui Url
 */
export const prefetchGetSapGuiUrlQuery = async <
  TData = Awaited<ReturnType<typeof getSapGuiUrl>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params: GetSapGuiUrlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSapGuiUrl>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetSapGuiUrlQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Sap Gui Url
 */
export const invalidateGetSapGuiUrl = async (
  queryClient: QueryClient,
  params: GetSapGuiUrlParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetSapGuiUrlQueryKey(params) }, options);

  return queryClient;
};

/**
 * Persist an ECC/S4 object + its subobjects as local Programs rows.
 *
 * Best-effort: per-item status is returned. UPSERT by `(adt_uri, sap_version,
 * created_by)` — re-saving the same object replaces its previous source and
 * subobject set. Filenames are persisted with a `[ECC]`/`[S4]` prefix so My
 * Programs distinguishes saves from local-origin programs.
 * @summary Save As Programs
 */
export const saveAsPrograms = (
  tLSaveAsProgramsRequest: BodyType<TLSaveAsProgramsRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSaveAsProgramsResponse>(
    {
      url: `/project-explorer/save-as-programs`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLSaveAsProgramsRequest,
      signal,
    },
    options,
  );
};

export const getSaveAsProgramsQueryKey = (tLSaveAsProgramsRequest?: BodyType<TLSaveAsProgramsRequest>) => {
  return ['POST', 'project-explorer', 'save-as-programs', tLSaveAsProgramsRequest] as const;
};

export const getSaveAsProgramsQueryOptions = <
  TData = Awaited<ReturnType<typeof saveAsPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSaveAsProgramsRequest: BodyType<TLSaveAsProgramsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof saveAsPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSaveAsProgramsQueryKey(tLSaveAsProgramsRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof saveAsPrograms>>> = ({ signal }) =>
    saveAsPrograms(tLSaveAsProgramsRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof saveAsPrograms>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SaveAsProgramsQueryResult = NonNullable<Awaited<ReturnType<typeof saveAsPrograms>>>;
export type SaveAsProgramsQueryError = ErrorType<TLHTTPValidationError>;

export function useSaveAsPrograms<
  TData = Awaited<ReturnType<typeof saveAsPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSaveAsProgramsRequest: BodyType<TLSaveAsProgramsRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof saveAsPrograms>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof saveAsPrograms>>,
          TError,
          Awaited<ReturnType<typeof saveAsPrograms>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSaveAsPrograms<
  TData = Awaited<ReturnType<typeof saveAsPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSaveAsProgramsRequest: BodyType<TLSaveAsProgramsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof saveAsPrograms>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof saveAsPrograms>>,
          TError,
          Awaited<ReturnType<typeof saveAsPrograms>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSaveAsPrograms<
  TData = Awaited<ReturnType<typeof saveAsPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSaveAsProgramsRequest: BodyType<TLSaveAsProgramsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof saveAsPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Save As Programs
 */

export function useSaveAsPrograms<
  TData = Awaited<ReturnType<typeof saveAsPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSaveAsProgramsRequest: BodyType<TLSaveAsProgramsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof saveAsPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSaveAsProgramsQueryOptions(tLSaveAsProgramsRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Save As Programs
 */
export const prefetchSaveAsProgramsQuery = async <
  TData = Awaited<ReturnType<typeof saveAsPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLSaveAsProgramsRequest: BodyType<TLSaveAsProgramsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof saveAsPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSaveAsProgramsQueryOptions(tLSaveAsProgramsRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Save As Programs
 */
export const invalidateSaveAsPrograms = async (
  queryClient: QueryClient,
  tLSaveAsProgramsRequest: BodyType<TLSaveAsProgramsRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getSaveAsProgramsQueryKey(tLSaveAsProgramsRequest) }, options);

  return queryClient;
};

/**
 * @summary List Transports
 */
export const listTransports = (
  params?: ListTransportsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLTransportListResponse>(
    { url: `/project-explorer/transports`, method: 'GET', params, signal },
    options,
  );
};

export const getListTransportsQueryKey = (params?: ListTransportsParams) => {
  return ['project-explorer', 'transports', ...(params ? [params] : [])] as const;
};

export const getListTransportsQueryOptions = <
  TData = Awaited<ReturnType<typeof listTransports>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListTransportsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTransports>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListTransportsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listTransports>>> = ({ signal }) =>
    listTransports(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listTransports>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListTransportsQueryResult = NonNullable<Awaited<ReturnType<typeof listTransports>>>;
export type ListTransportsQueryError = ErrorType<TLHTTPValidationError>;

export function useListTransports<
  TData = Awaited<ReturnType<typeof listTransports>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListTransportsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTransports>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTransports>>,
          TError,
          Awaited<ReturnType<typeof listTransports>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListTransports<
  TData = Awaited<ReturnType<typeof listTransports>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListTransportsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTransports>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTransports>>,
          TError,
          Awaited<ReturnType<typeof listTransports>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListTransports<
  TData = Awaited<ReturnType<typeof listTransports>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListTransportsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTransports>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Transports
 */

export function useListTransports<
  TData = Awaited<ReturnType<typeof listTransports>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListTransportsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTransports>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListTransportsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Transports
 */
export const prefetchListTransportsQuery = async <
  TData = Awaited<ReturnType<typeof listTransports>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListTransportsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTransports>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListTransportsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Transports
 */
export const invalidateListTransports = async (
  queryClient: QueryClient,
  params?: ListTransportsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListTransportsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Create Transport
 */
export const createTransport = (
  tLTransportCreateRequest: BodyType<TLTransportCreateRequest>,
  params?: CreateTransportParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLTransportInfo>(
    {
      url: `/project-explorer/transports`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLTransportCreateRequest,
      params,
      signal,
    },
    options,
  );
};

export const getCreateTransportQueryKey = (
  tLTransportCreateRequest?: BodyType<TLTransportCreateRequest>,
  params?: CreateTransportParams,
) => {
  return ['POST', 'project-explorer', 'transports', ...(params ? [params] : []), tLTransportCreateRequest] as const;
};

export const getCreateTransportQueryOptions = <
  TData = Awaited<ReturnType<typeof createTransport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLTransportCreateRequest: BodyType<TLTransportCreateRequest>,
  params?: CreateTransportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTransport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateTransportQueryKey(tLTransportCreateRequest, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createTransport>>> = ({ signal }) =>
    createTransport(tLTransportCreateRequest, params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createTransport>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateTransportQueryResult = NonNullable<Awaited<ReturnType<typeof createTransport>>>;
export type CreateTransportQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateTransport<
  TData = Awaited<ReturnType<typeof createTransport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLTransportCreateRequest: BodyType<TLTransportCreateRequest>,
  params: undefined | CreateTransportParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTransport>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createTransport>>,
          TError,
          Awaited<ReturnType<typeof createTransport>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateTransport<
  TData = Awaited<ReturnType<typeof createTransport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLTransportCreateRequest: BodyType<TLTransportCreateRequest>,
  params?: CreateTransportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTransport>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createTransport>>,
          TError,
          Awaited<ReturnType<typeof createTransport>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateTransport<
  TData = Awaited<ReturnType<typeof createTransport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLTransportCreateRequest: BodyType<TLTransportCreateRequest>,
  params?: CreateTransportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTransport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Transport
 */

export function useCreateTransport<
  TData = Awaited<ReturnType<typeof createTransport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLTransportCreateRequest: BodyType<TLTransportCreateRequest>,
  params?: CreateTransportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTransport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateTransportQueryOptions(tLTransportCreateRequest, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Transport
 */
export const prefetchCreateTransportQuery = async <
  TData = Awaited<ReturnType<typeof createTransport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLTransportCreateRequest: BodyType<TLTransportCreateRequest>,
  params?: CreateTransportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createTransport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateTransportQueryOptions(tLTransportCreateRequest, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Transport
 */
export const invalidateCreateTransport = async (
  queryClient: QueryClient,
  tLTransportCreateRequest: BodyType<TLTransportCreateRequest>,
  params?: CreateTransportParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateTransportQueryKey(tLTransportCreateRequest, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Transport
 */
export const getTransport = (
  { transport }: GetTransportPathParameters,
  params?: GetTransportParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLTransportInfo>(
    { url: `/project-explorer/transports/${transport}`, method: 'GET', params, signal },
    options,
  );
};

export const getGetTransportQueryKey = ({ transport }: GetTransportPathParameters, params?: GetTransportParams) => {
  return ['project-explorer', 'transports', transport, ...(params ? [params] : [])] as const;
};

export const getGetTransportQueryOptions = <
  TData = Awaited<ReturnType<typeof getTransport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { transport }: GetTransportPathParameters,
  params?: GetTransportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTransport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTransportQueryKey({ transport }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTransport>>> = ({ signal }) =>
    getTransport({ transport }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: transport !== null && transport !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getTransport>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetTransportQueryResult = NonNullable<Awaited<ReturnType<typeof getTransport>>>;
export type GetTransportQueryError = ErrorType<TLHTTPValidationError>;

export function useGetTransport<
  TData = Awaited<ReturnType<typeof getTransport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetTransportPathParameters,
  params: undefined | GetTransportParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTransport>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTransport>>,
          TError,
          Awaited<ReturnType<typeof getTransport>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTransport<
  TData = Awaited<ReturnType<typeof getTransport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetTransportPathParameters,
  params?: GetTransportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTransport>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTransport>>,
          TError,
          Awaited<ReturnType<typeof getTransport>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTransport<
  TData = Awaited<ReturnType<typeof getTransport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetTransportPathParameters,
  params?: GetTransportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTransport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Transport
 */

export function useGetTransport<
  TData = Awaited<ReturnType<typeof getTransport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { transport }: GetTransportPathParameters,
  params?: GetTransportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTransport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetTransportQueryOptions({ transport }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Transport
 */
export const prefetchGetTransportQuery = async <
  TData = Awaited<ReturnType<typeof getTransport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { transport }: GetTransportPathParameters,
  params?: GetTransportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTransport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetTransportQueryOptions({ transport }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Transport
 */
export const invalidateGetTransport = async (
  queryClient: QueryClient,
  { transport }: GetTransportPathParameters,
  params?: GetTransportParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetTransportQueryKey({ transport }, params) }, options);

  return queryClient;
};
